import React, { useEffect, useState } from "react";
import { Row, Col, Table } from "rsuite";

const { Column, HeaderCell, Cell } = Table;

interface DataPoint {
	itemDesc: string;
	itemId: string;
	divisionId: string;
	volume: number;
	revenue: number;
	margin: number;
	opportunity: number;
}

interface SummaryData {
	itemId: string;
	itemDesc: string;
	vol: number;
	rev: number;
	marg: number;
	opport: number;
}

interface AggregatedData {
	vol: number;
	rev: number;
	marg: number;
	opport: number;
}

interface ItemLevelData {
	[key: number]: any;
}

interface ExtendedRows {
	[key: number]: any[];
}

interface DivisionWiseTableProps {
	itemLevelData: ItemLevelData;
	extendedRows: ExtendedRows;
}

const DivisionWiseTable: React.FC<DivisionWiseTableProps> = ({
	itemLevelData,
	extendedRows,
}) => {
	const [tableHeight, setTableHeight] = useState<number>(250);
	const [dataPoints, setDataPoints] = useState<DataPoint[]>([]);
	const [combinelist, setCombineList] = useState<DataPoint[]>([]);

	useEffect(() => {
		const updateTableHeight = () => {
			setTableHeight(window.innerHeight - 220 - 405);
		};
		updateTableHeight();
		window.addEventListener("resize", updateTableHeight);

		return () => {
			window.removeEventListener("resize", updateTableHeight);
		};
	}, []);
	useEffect(() => {
		const combinedList = Object.values(itemLevelData).flat();
		const currentDataPoints: DataPoint[] = combinedList.map((item) => ({
			itemDesc: item.itemDesc,
			itemId: item.itemId,
			divisionId: item.divisionId,
			volume: item.volume,
			revenue: item.revenue,
			margin: item.margin,
			opportunity: item.opportunity,
		}));
		setCombineList(combinedList);
		setDataPoints(currentDataPoints);
	}, [itemLevelData]);
	const setDataStructure = (data: DataPoint[]) => {
		const obj: { [key: string]: AggregatedData } = {};

		data.forEach((d) => {
			if (d?.divisionId) {
				if (!obj[d.divisionId]) {
					obj[d.divisionId] = {
						vol: 0,
						rev: 0,
						marg: 0,
						opport: 0,
					};
				}

				obj[d.divisionId].vol += d.volume || 0;
				obj[d.divisionId].rev += d.revenue || 0;
				obj[d.divisionId].marg += d.margin || 0;
				obj[d.divisionId].opport += d.opportunity || 0;
			}
		});

		return Object.entries(obj)
			.filter(([_, v]) => v !== undefined)
			.sort((a, b) => b[1].opport - a[1].opport);
	};

	const thousands = (value: number) =>
		`${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

	const refreshedData = setDataStructure(dataPoints);
	const summaryData: SummaryData =
		refreshedData.length > 0
			? {
				itemId: combinelist[0].itemId,
				itemDesc: combinelist[1].itemDesc,
				vol: refreshedData.reduce(
					(sum, division) => sum + division[1].vol,
					0
				),
				rev: refreshedData.reduce(
					(sum, division) => sum + division[1].rev,
					0
				),
				marg: refreshedData.reduce(
					(sum, division) => sum + division[1].marg,
					0
				),
				opport: refreshedData.reduce(
					(sum, division) => sum + division[1].opport,
					0
				),
			}
			: {
				itemId: "",
				itemDesc: "",
				vol: 0,
				rev: 0,
				marg: 0,
				opport: 0,
			};
	const attributes = [
		{ key: "vol", label: "A: Volume" },
		{ key: "rev", label: "B: Revenue" },
		{ key: "marg", label: "C: Margin ($)" },
		{ key: "opport", label: "D: Opportunity" },
	];

	return (

		<div className="h-[87%]">
			{dataPoints.length ? (
				<>
					<Table
						className="table-container"
						fillHeight
						data={attributes}
						wordWrap
						bordered
						rowHeight={20}
						rowClassName={(rowData, rowIndex) => {
							if (!rowData) return "";
							let className = "rs-table-row";

							if (rowIndex % 2 === 0) {
								className += " db-tablebg";
							}

							return className;
						}}
					>
						<Column
							minWidth={90}
							flexGrow={2}
							align="center"
							verticalAlign="middle"
							fixed
						>
							<HeaderCell><div className="table-header-text font-semibold">Attribute</div></HeaderCell>
							<Cell dataKey="label">
								{(rowData) => (
									<div className="table-cell-text header-bold">
										{rowData["label"]}
									</div>
								)}
							</Cell>
						</Column>

						{refreshedData
							.filter((d) => d && d[0])
							.map((d) => (
								<Column
									flexGrow={1}
									key={d[0]}
									verticalAlign="middle"
								>
									<HeaderCell align="center">
										<div className="table-header-text font-semibold">
											Div-{d[0]}
										</div>
									</HeaderCell>
									<Cell align="center">
										{(rowData) => {
											const data: any =
												refreshedData.find(
													(item) =>
														item[0] === d[0]
												);
											const value =
												data[1][rowData.key];

											return <div className="table-cell-text">{rowData.key === "vol"
												? thousands(
													Math.round(value)
												)
												: `$${value < 1000
													? Math.round(
														value *
														10
													) / 10
													: thousands(
														Math.round(
															value /
															1000
														)
													) + "K"
												}`}</div>;
										}}
									</Cell>
								</Column>
							))}
					</Table>
					<div className="pl-[1vh] pt-[1vh] text-[0.75vw]">
						<span>
							<label className="footerDivTable text-[0.75vw]">
								<span className="header-bold text-[0.75vw]">Item:</span>{" "}
								{summaryData?.itemId} - {summaryData?.itemDesc}
							</label>
						</span>
						<span>
							<label className="footerDivTable ml-[1vw] text-[0.75vw]">
								<span className="header-bold text-[0.75vw]">Volume:</span>{" "}
								{summaryData?.vol < 1000
									? Math.round(summaryData?.vol * 10) / 10
									: thousands(
										Math.round(summaryData?.vol / 1000)
									) + "K"}
							</label>
							<label className="footerDivTable ml-[1vw] text-[0.75vw]">
								<span className="header-bold text-[0.75vw]">Revenue:</span> $
								{summaryData?.rev < 1000
									? Math.round(summaryData?.rev * 10) / 10
									: thousands(
										Math.round(summaryData?.rev / 1000)
									) + "K"}
							</label>
							<label className="footerDivTable ml-[1vw] text-[0.75vw]">
								<span className="header-bold text-[0.75vw]">Margin:</span> $
								{summaryData?.marg < 1000
									? Math.round(summaryData?.marg * 10) / 10
									: thousands(
										Math.round(summaryData?.marg / 1000)
									) + "K"}
							</label>
							<label className="footerDivTable ml-[1vw] text-[0.75vw]">
								<span className="header-bold text-[0.75vw]">
									Opportunity:
								</span>{" "}
								$
								{summaryData?.opport < 1000
									? Math.round(summaryData?.opport * 10) / 10
									: thousands(
										Math.round(
											summaryData?.opport / 1000
										)
									) + "K"}
							</label>
						</span>
					</div>
				</>
			) : (
				<span className="h-full flex justify-center items-center italic font-normal text-[2.5vh] text-center text-black">
					Click on the left Table to get the Detailed view
				</span>
			)}
		</div>
	);
};

export default DivisionWiseTable;
